import React, { useEffect, useState, useRef } from 'react'
import clsx from 'clsx'
import Modal from 'react-modal'
import { ReactComponent as Arrow } from '@/assets/icons/lng-arrow.svg'
import { ReactComponent as Close } from '@/assets/icons/close-modal.svg'
import { LanguageSwitchProps } from './types'
import * as styles from './LanguageSwitch.module.css'
import { SwitchElement } from './SwitchElement/SwitchElement'

export function LanguageSwitch({
  languages,
  currentLang,
  showCode = true,
  alternates,
  className
}: LanguageSwitchProps) {
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const ref = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [openModal, setOpenModal] = useState(false)
  const [numOptions] = useState(languages?.length)

  const closeMenu = () => {
    setExpanded(false)
    setSelectedIndex(-1)
  }

  const escapeListener = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeMenu()
    }
  }

  const clickListener = (e: any) => {
    if (!ref.current?.contains(e.target)) {
      closeMenu()
    }
  }

  useEffect(() => {
    document.addEventListener('click', clickListener)
    document.addEventListener('keyup', escapeListener)
  }, [])

  const onClickHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    setExpanded(!expanded)
  }

  const openModalHandler = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    setOpenModal(!openModal)
  }

  const updateFocusIndexBy = (delta: number) => {
    let index = selectedIndex
    index += delta
    if (index > numOptions - 1) {
      setSelectedIndex(0)
    } else if (index < 0) {
      setSelectedIndex(numOptions - 1)
    } else {
      setSelectedIndex(index)
    }
  }

  const moveSelectionPrev = (e: React.KeyboardEvent) => {
    e.preventDefault()
    updateFocusIndexBy(-1)
  }

  const moveSelectionNext = (e: React.KeyboardEvent) => {
    e.preventDefault()
    updateFocusIndexBy(1)
  }

  const handleKeys = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const keys: { [key: string]: (event: React.KeyboardEvent) => void } = {
      ArrowDown: moveSelectionNext,
      ArrowUp: moveSelectionPrev,
      Escape: closeMenu
    }

    if (keys[e.key] && expanded) {
      keys[e.key](e)
    }
  }

  return (
    <div
      ref={ref}
      onKeyDown={handleKeys}
      className={clsx('netbook:relative p-0 mt-auto netbook:my-0 flex flex-col netbook:block', className)}
      role="none"
    >
      <span
        className={clsx(styles.hiddenLabel, 'absolute p-0 overflow-hidden whitespace-nowrap border-0')}
        id="lang_elem"
      >
        change language
      </span>
      {/* desktop button */}
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={expanded}
        aria-labelledby="lang_elem lang_button"
        id="lang_button"
        tabIndex={0}
        onClick={onClickHandler}
        className="uppercase hidden netbook:block box-border text-left text-text-dark-900  bg-transparent font-normal whitespace-nowrap text-bodyM"
      >
        <div className="flex justify-start gap-4 items-center">
          {showCode ? currentLang?.code : currentLang.name}
          <span className={clsx('[&>svg]:size-16 transition', expanded && 'rotate-180')}>
            <Arrow />
          </span>
        </div>
      </button>

      {/* mobile button */}
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={expanded}
        aria-labelledby="lang_elem lang_button"
        id="lang_button"
        tabIndex={0}
        onClick={openModalHandler}
        className="uppercase  box-border text-left text-text-dark-900 block netbook:hidden bg-transparent font-normal whitespace-nowrap text-bodyM"
      >
        <div className="flex justify-start gap-4 items-center">
          {showCode ? currentLang?.code : currentLang.name}
          <span className={clsx('[&>svg]:size-16 transition', openModal && 'rotate-180')}>
            <Arrow />
          </span>
        </div>
      </button>

      {/* desktop popup */}
      <ul
        role="listbox"
        aria-labelledby="lang_elem"
        hidden={!expanded}
        tabIndex={-1}
        className="absolute left-0 bottom-0 netbook:bottom-full bg-white z-50 rounded-md shadow-lang p-12 w-full netbook:mb-16"
      >
        {languages?.map((opt: LanguageType) => (
          <SwitchElement
            key={opt?.code}
            option={opt}
            onOptionClick={closeMenu}
            active={languages[selectedIndex]?.code ?? ''}
            alternates={alternates}
            currentLang={currentLang}
          />
        ))}
      </ul>

      {/* mobile modal */}
      <Modal
        isOpen={openModal}
        className={styles.modal}
        portalClassName="modal"
        overlayClassName={styles.overlay}
        onRequestClose={() => setOpenModal(false)}
      >
        <button
          aria-label="close modal"
          type="button"
          className="text-bodyS text-text-dark-900 ml-auto flex w-fit pt-24 pr-24"
          onClick={() => setOpenModal(false)}
        >
          <Close />
        </button>
        <ul
          role="listbox"
          aria-labelledby="lang_elem"
          hidden={!openModal}
          tabIndex={-1}
          className="bg-white z-0 rounded-md shadow-lang p-24 w-full"
        >
          {languages?.map((opt: LanguageType) => (
            <SwitchElement
              key={opt?.code}
              option={opt}
              onOptionClick={closeMenu}
              active={languages[selectedIndex]?.code ?? ''}
              alternates={alternates}
              currentLang={currentLang}
            />
          ))}
        </ul>
      </Modal>
    </div>
  )
}
